"use client";

import { track } from "@/lib/analytics/client";
import { useCallback, useState } from "react";

export type ClubType = "non-spond" | "spond";

export type ContactPreference = "email" | "phone";

export type ListMyClubFormData = {
  clubType: ClubType;
  clubName: string;
  contactName: string;
  email: string;
  phone: string;
  contactPreference: ContactPreference;
  role: string;
  referralSource: string;
};

export type FormStatus = "idle" | "submitting" | "success" | "error";

const INITIAL_FORM_DATA: ListMyClubFormData = {
  clubType: "non-spond",
  clubName: "",
  contactName: "",
  email: "",
  phone: "",
  contactPreference: "email",
  role: "",
  referralSource: "",
};

export function useListMyClubForm(onOpenChange: (open: boolean) => void) {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [contactError, setContactError] = useState("");
  const [roleError, setRoleError] = useState("");
  const [formData, setFormData] =
    useState<ListMyClubFormData>(INITIAL_FORM_DATA);

  const getTrackingProps = useCallback(() => {
    return {
      club_type: formData.clubType,
      has_email: Boolean(formData.email),
      has_phone: Boolean(formData.phone),
      has_role: Boolean(formData.role),
      has_referral: Boolean(formData.referralSource),
    };
  }, [
    formData.clubType,
    formData.email,
    formData.phone,
    formData.role,
    formData.referralSource,
  ]);

  const validateContactMethod = useCallback((): boolean => {
    return Boolean(formData.email.trim() || formData.phone.trim());
  }, [formData.email, formData.phone]);

  const validateRole = useCallback((): boolean => {
    return formData.clubType !== "non-spond" || Boolean(formData.role.trim());
  }, [formData.clubType, formData.role]);

  const submitForm = useCallback(async (): Promise<void> => {
    setStatus("submitting");
    setErrorMessage("");

    const response = await fetch(`${basePath}/api/list-my-club`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    const data = await response.json();

    if (response.ok) {
      setStatus("success");
      track("Submit list my club form", {
        ...getTrackingProps(),
        status: "success",
      });
    } else {
      setStatus("error");
      setErrorMessage(data.error);
      track("Submit list my club form", {
        ...getTrackingProps(),
        status: "error",
        error_message:
          typeof data.error === "string" ? data.error : "unknown_error",
      });
    }
  }, [basePath, formData, getTrackingProps]);

  const handleSubmit = useCallback(
    async (
      e: React.FormEvent<HTMLFormElement>,
      contactErrorMessage?: string,
      roleErrorMessage?: string,
    ) => {
      e.preventDefault();
      setContactError("");
      setRoleError("");

      if (!validateContactMethod()) {
        setContactError(contactErrorMessage ?? "Email or phone is required");
        track("Submit list my club form validation failed", {
          reason: "missing_contact_info",
        });
        return;
      }

      if (!validateRole()) {
        setRoleError(roleErrorMessage ?? "Role is required");
        track("Submit list my club form validation failed", {
          reason: "missing_role",
        });
        return;
      }

      try {
        await submitForm();
      } catch {
        setStatus("error");
        track("Submit list my club form", {
          ...getTrackingProps(),
          status: "error",
          error_message: "network_error",
        });
      }
    },
    [validateContactMethod, validateRole, submitForm, getTrackingProps],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if ((name === "email" || name === "phone") && contactError) {
        setContactError("");
      }
    },
    [contactError],
  );

  const handleFieldChange = useCallback(
    <K extends keyof ListMyClubFormData>(
      field: K,
      value: ListMyClubFormData[K],
    ) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (field === "role" && roleError) {
        setRoleError("");
      }
      if (field === "clubType" && value === "spond" && roleError) {
        setRoleError("");
      }
    },
    [roleError],
  );

  const resetForm = useCallback(() => {
    setStatus("idle");
    setErrorMessage("");
    setContactError("");
    setRoleError("");
    setFormData(INITIAL_FORM_DATA);
  }, []);

  const handleClose = useCallback(() => {
    onOpenChange(false);
    setTimeout(resetForm, 200);
  }, [onOpenChange, resetForm]);

  return {
    status,
    errorMessage,
    contactError,
    roleError,
    formData,
    handleSubmit,
    handleChange,
    handleFieldChange,
    handleClose,
  };
}
